import React, { useState, useEffect } from 'react';
import { videosAPI, channelsAPI, commentsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Loading from '../../components/common/Loading';
import { useForm } from 'react-hook-form';
import { formatDateShort } from '../../utils/formatDate';

const VideosList = () => {
  const { isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [viewingVideo, setViewingVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [videoUrlModalOpen, setVideoUrlModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [tempVideoUrl, setTempVideoUrl] = useState('');

  const { register, handleSubmit, reset, watch, setValue } = useForm();

  useEffect(() => {
    fetchChannels();
    fetchVideos();
  }, [search, statusFilter, channelFilter]);

  const fetchChannels = async () => {
    try {
      const response = await channelsAPI.getAll({ limit: 100 });
      setChannels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channelId = channelFilter;
      const response = await videosAPI.getAll(params);
      setVideos(response.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des vidéos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingVideo(null);
    reset({
      status: 'pending' // Statut par défaut : En attente
    });
    setModalOpen(true);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    reset({
      ...video,
      channelId: video.channelId?._id || video.channelId,
      plannedDate: video.plannedDate ? new Date(video.plannedDate).toISOString().split('T')[0] : '',
      resource: video.resource || '',
      videoUrl: video.videoUrl || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Supprimer la vidéo ${video.title}?`)) return;

    try {
      await videosAPI.delete(video._id || video.id);
      toast.success('Vidéo supprimée');
      fetchVideos();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleView = async (video) => {
    setViewingVideo(video);
    setViewModalOpen(true);
    if (video) {
      fetchComments(video._id || video.id);
    }
  };

  const fetchComments = async (videoId) => {
    try {
      setLoadingComments(true);
      const response = await commentsAPI.getByVideo(videoId);
      setComments(response.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des commentaires');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;

    try {
      await commentsAPI.delete(commentId);
      toast.success('Commentaire supprimé');
      if (viewingVideo) {
        fetchComments(viewingVideo._id || viewingVideo.id);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const onSubmit = async (data) => {
    try {
      // Retirer videoUrl des données lors de la création
      if (!editingVideo) {
        delete data.videoUrl;
        data.status = 'pending'; // Forcer le statut à "pending" lors de la création
      }
      
      if (editingVideo) {
        await videosAPI.update(editingVideo._id || editingVideo.id, data);
        toast.success('Vidéo modifiée');
      } else {
        await videosAPI.create(data);
        toast.success('Vidéo créée');
      }
      setModalOpen(false);
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur');
    }
  };

  const handleStatusChangeInForm = (newStatus, currentStatus = null) => {
    const actualCurrentStatus = currentStatus || watch('status') || (editingVideo?.status || 'pending');
    
    // Si on change de "pending" à "done", ouvrir le modal pour l'URL
    if (actualCurrentStatus === 'pending' && newStatus === 'done') {
      setPendingStatusChange(newStatus);
      setTempVideoUrl(editingVideo?.videoUrl || '');
      setVideoUrlModalOpen(true);
    } else {
      // Sinon, mettre à jour directement
      setValue('status', newStatus);
    }
  };

  const handleVideoUrlModalSubmit = () => {
    // Mettre à jour le statut et l'URL
    setValue('status', pendingStatusChange);
    if (tempVideoUrl.trim()) {
      setValue('videoUrl', tempVideoUrl.trim());
    }
    setVideoUrlModalOpen(false);
    setPendingStatusChange(null);
    setTempVideoUrl('');
  };

  const handleVideoUrlModalCancel = () => {
    // Annuler le changement de statut
    setVideoUrlModalOpen(false);
    setPendingStatusChange(null);
    setTempVideoUrl('');
  };

  const columns = [
    { header: 'Titre', accessor: 'title' },
    {
      header: 'Chaîne',
      accessor: 'channelId',
      render: (video) => video.channelId?.name || '-',
    },
    {
      header: 'Statut',
      accessor: 'status',
      render: (video) => (
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          background: video.status === 'done' ? '#10b981' : '#f59e0b',
          color: 'white',
        }}>
          {video.status}
        </span>
      ),
    },
    {
      header: 'Date prévue',
      accessor: 'plannedDate',
      render: (video) => formatDateShort(video.plannedDate),
    },
    {
      header: 'Ressource',
      accessor: 'resource',
      render: (video) => video.resource ? (
        <span style={{ 
          maxWidth: '200px', 
          display: 'block', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }} title={video.resource}>
          {video.resource}
        </span>
      ) : '-',
    },
  ];

  if (loading) return <Loading />;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Vidéos</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          ➕ Créer une vidéo
        </button>
      </div>

      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          style={styles.select}
        >
          <option value="">Toutes les chaînes</option>
          {channels.map((channel) => (
            <option key={channel._id || channel.id} value={channel._id || channel.id}>
              {channel.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="done">Terminées</option>
        </select>
      </div>

      <Table
        columns={columns}
        data={videos}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingVideo ? 'Modifier la vidéo' : 'Nouvelle vidéo'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          <div style={styles.formGroup}>
            <label>Chaîne</label>
            <select {...register('channelId', { required: true })} className="input">
              <option value="">Sélectionner une chaîne</option>
              {channels.map((channel) => (
                <option key={channel._id || channel.id} value={channel._id || channel.id}>
                  {channel.name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label>Titre</label>
            <input {...register('title', { required: true })} className="input" />
          </div>
          <div style={styles.formGroup}>
            <label>Description</label>
            <textarea
              {...register('description', { required: true })}
              className="input"
              rows="6"
            />
          </div>
          {editingVideo && (
            <div style={styles.formGroup}>
              <label>URL de la vidéo YouTube (optionnel)</label>
              <input
                type="url"
                {...register('videoUrl')}
                className="input"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
          )}
          <div style={styles.formGroup}>
            <label>Date prévue</label>
            <input
              type="date"
              {...register('plannedDate')}
              className="input"
            />
          </div>
          <div style={styles.formGroup}>
            <label>Statut</label>
            <select 
              {...register('status', { 
                value: editingVideo ? (editingVideo.status || 'pending') : 'pending'
              })} 
              className="input"
              defaultValue={editingVideo ? (editingVideo.status || 'pending') : 'pending'}
              onChange={(e) => {
                const newStatus = e.target.value;
                const currentStatus = watch('status') || (editingVideo?.status || 'pending');
                handleStatusChangeInForm(newStatus, currentStatus);
              }}
              disabled={editingVideo?.status === 'done' && editingVideo?.videoUrl}
            >
              <option value="pending">En attente</option>
              <option value="done">Terminée</option>
            </select>
            {editingVideo?.status === 'done' && editingVideo?.videoUrl && (
              <small style={styles.helpText}>
                ⚠️ Le statut ne peut pas être modifié après avoir été marqué comme terminé avec une URL.
              </small>
            )}
          </div>
          <div style={styles.formGroup}>
            <label>Ressource</label>
            <textarea
              {...register('resource')}
              className="input"
              rows="4"
              placeholder="Liens, notes ou autres ressources pour cette vidéo..."
            />
          </div>
          <div style={styles.formActions}>
            <button type="button" onClick={() => setModalOpen(false)} className="btn btn-outline">
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              {editingVideo ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal pour l'URL de la vidéo lors du changement de statut */}
      <Modal
        isOpen={videoUrlModalOpen}
        onClose={handleVideoUrlModalCancel}
        title="URL de la vidéo (optionnel)"
      >
        <div style={styles.form}>
          <div style={styles.formGroup}>
            <label>URL de la vidéo YouTube</label>
            <input
              type="url"
              value={tempVideoUrl}
              onChange={(e) => setTempVideoUrl(e.target.value)}
              className="input"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <small style={styles.helpText}>
              Ce champ est optionnel. Vous pouvez l'ajouter plus tard.
            </small>
          </div>
          <div style={styles.formActions}>
            <button 
              type="button" 
              onClick={handleVideoUrlModalCancel} 
              className="btn btn-outline"
            >
              Annuler
            </button>
            <button 
              type="button" 
              onClick={handleVideoUrlModalSubmit} 
              className="btn btn-primary"
            >
              Confirmer
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de visualisation */}
      <Modal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingVideo(null);
          setComments([]);
        }}
        title={viewingVideo?.title || 'Détails de la vidéo'}
        size="lg"
      >
        {viewingVideo && (
          <div style={styles.viewDetails}>
            <div style={styles.detailRow}>
              <strong>Chaîne:</strong> {viewingVideo.channelId?.name || '-'}
            </div>
            <div style={styles.detailRow}>
              <strong>Statut:</strong>
              <span style={{
                ...styles.statusBadge,
                background: viewingVideo.status === 'done' ? '#10b981' : '#f59e0b',
              }}>
                {viewingVideo.status}
              </span>
            </div>
            <div style={styles.detailRow}>
              <strong>Date prévue:</strong> {formatDateShort(viewingVideo.plannedDate)}
            </div>
            <div style={styles.detailRow}>
              <strong>Description:</strong>
              <p style={styles.descriptionText}>{viewingVideo.description}</p>
            </div>
            <div style={styles.detailRow}>
              <strong>URL de la vidéo:</strong>
              {viewingVideo.videoUrl ? (
                <div style={styles.videoUrlContainer}>
                  <a
                    href={viewingVideo.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.videoLink}
                  >
                    {viewingVideo.videoUrl}
                  </a>
                </div>
              ) : (
                <div style={styles.missingValue}>N’existe pas</div>
              )}
            </div>
            {viewingVideo.resource && (
              <div style={styles.detailRow}>
                <strong>Ressource:</strong>
                <div style={styles.resourceContent}>
                  {viewingVideo.resource.split('\n').map((line, idx) => (
                    <p key={idx} style={{ margin: '0.25rem 0' }}>{line}</p>
                  ))}
                </div>
              </div>
            )}
            {viewingVideo.resources && viewingVideo.resources.length > 0 && (
              <div style={styles.detailRow}>
                <strong>Ressources liées:</strong>
                <ul style={styles.resourcesList}>
                  {viewingVideo.resources.map((resource) => (
                    <li key={resource._id || resource.id}>{resource.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Section Commentaires */}
            <div style={styles.commentsSection}>
              <h3 style={styles.commentsTitle}>Notes et Commentaires</h3>
              
              {loadingComments ? (
                <div style={styles.loading}>Chargement...</div>
              ) : comments.length === 0 ? (
                <div style={styles.noComments}>Aucun commentaire</div>
              ) : (
                <div style={styles.commentsList}>
                  {comments.map((comment) => (
                    <div key={comment._id || comment.id} style={styles.commentItem}>
                      <div style={styles.commentHeader}>
                        <div>
                          <strong style={styles.commentAuthor}>
                            {comment.userId?.fullName || 'Utilisateur'}
                          </strong>
                          <span style={styles.commentDate}>
                            {formatDateShort(comment.createdAt)}
                          </span>
                        </div>
                        {isAdmin() && (
                          <button
                            onClick={() => handleDeleteComment(comment._id || comment.id)}
                            style={styles.deleteCommentBtn}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <p style={styles.commentText}>{comment.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--text)',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  searchInput: {
    flex: 1,
    maxWidth: '400px',
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  },
  select: {
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  formActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  viewDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  detailRow: {
    padding: '0.75rem 0',
    borderBottom: '1px solid var(--border)',
  },
  statusBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'white',
    marginLeft: '0.5rem',
  },
  descriptionText: {
    marginTop: '0.5rem',
    lineHeight: 1.6,
    color: 'var(--text)',
  },
  videoUrlContainer: {
    marginTop: '0.5rem',
  },
  videoLink: {
    color: 'var(--primary)',
    textDecoration: 'none',
    wordBreak: 'break-all',
  },
  missingValue: {
    marginTop: '0.5rem',
    color: 'var(--text-light)',
    fontStyle: 'italic',
  },
  resourceContent: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: 'var(--light)',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    marginTop: '0.5rem',
  },
  resourcesList: {
    marginTop: '0.5rem',
    paddingLeft: '1.5rem',
  },
  commentsSection: {
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: '2px solid var(--border)',
  },
  commentsTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: 'var(--text)',
  },
  commentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  commentItem: {
    background: 'var(--light)',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--border)',
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '0.5rem',
  },
  commentAuthor: {
    fontSize: '0.875rem',
    color: 'var(--text)',
    display: 'block',
    marginBottom: '0.25rem',
  },
  commentDate: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
  },
  commentText: {
    fontSize: '0.875rem',
    color: 'var(--text)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  deleteCommentBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.25rem',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
  },
  noComments: {
    textAlign: 'center',
    color: 'var(--text-light)',
    padding: '2rem',
    fontSize: '0.875rem',
  },
  loading: {
    textAlign: 'center',
    color: 'var(--text-light)',
    padding: '1rem',
  },
  helpText: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
    marginTop: '0.25rem',
  },
};

export default VideosList;





